import React, { useCallback, useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { DesignSystemTheme } from '@minecraft.themes';
import { MediaVideoProps } from './types';

export type MediaVideoComponentProps = Pick<MediaVideoProps, 'src' | 'poster'> & {
  /** Defaults to `true`. */
  controls?: boolean;
  /** Defaults to `'auto'`. */
  preload?: 'none' | 'metadata' | 'auto';
  /** whether the video will play automatically when loaded, defaults to false */
  autoPlay?: boolean;
  /**
   * When toggled to `false` the component pauses the underlying `<video>`.
   * Default: `true`.
   */
  isVisible?: boolean;
  onPlay?: React.ReactEventHandler<HTMLVideoElement>;
  onPause?: React.ReactEventHandler<HTMLVideoElement>;
  onEnded?: React.ReactEventHandler<HTMLVideoElement>;
  /** Sets the `data-qa-id` attribute (Playwright selector). */
  'data-qa-id'?: string;
  /** Sets the `data-testid` attribute (Jest selector). */
  'data-testid'?: string;
  /** sets the background color of the video element if the video does not fill it completely */
  bgColor?: keyof DesignSystemTheme['bgColors'];
};

/**
 * `MediaVideo` — pure presentational `<video>` driven by a URL +
 * a poster image.
 *
 * The video sizes responsively to its container — there are no sizing
 * props on this component.
 *
 * Setting `isVisible={false}` pauses the underlying `<video>` (ported
 * from the existing `CloudinaryVideoAudio` effect). `onPlay` / `onPause`
 * / `onEnded` are wired to the native events.
 */
export const MediaVideo = React.forwardRef<HTMLVideoElement, MediaVideoComponentProps>(
  (
    {
      src,
      poster,
      autoPlay = false,
      preload = 'auto',
      controls = true,
      isVisible = true,
      onPlay,
      onPause,
      onEnded,
      'data-qa-id': qaId,
      'data-testid': testId,
      bgColor,
    },
    forwardedRef
  ) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const theme = useTheme();

    // Keep an internal ref for component behavior while also supporting an
    // optional consumer ref (object or callback) that points to the same node.
    const setVideoRef = useCallback(
      (node: HTMLVideoElement | null) => {
        const consumerRef = forwardedRef;

        videoRef.current = node;

        if (typeof consumerRef === 'function') {
          consumerRef(node);
        } else if (consumerRef) {
          consumerRef.current = node;
        }
      },
      [forwardedRef]
    );

    // This effect always uses the internal ref so visibility behavior works
    // regardless of whether a consumer passes a ref.
    useEffect(() => {
      if (!isVisible) {
        videoRef.current?.pause();
      }
    }, [isVisible]);

    return (
      // Timed-text assets are feature-specific; consumers must pair audio-only
      // media with an accessible transcript or equivalent nearby text.
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        ref={setVideoRef}
        src={src}
        poster={poster}
        autoPlay={controls ? autoPlay : false}
        preload={controls ? preload : 'none'}
        controls={controls}
        controlsList="nodownload"
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        style={{ backgroundColor: theme.designSystem.bgColors[bgColor ?? 'maxContrast'] }}
        className="cn_w-full cn_max-w-full cn_aspect-16-9"
        {...(qaId ? { 'data-qa-id': qaId } : {})}
        {...(testId ? { 'data-testid': testId } : {})}
      />
    );
  }
);

MediaVideo.displayName = 'MediaVideo';
